import { Action, createReducer, on } from '@ngrx/store';
import * as settingsActions from './settings.actions';
import { IPagination } from 'src/app/shared/models/page.model';
import { ICustomField, ILabel, ILabelOption, IModule, INotification, IObjectCustomFields, IOrdersSettings, IRole, ISplittingRule, ITeam, ITeamOption, IUser } from '../models/settings.models';

export interface SettingsState {
  me: IUser | null;
  allUsers: IUser[];
  metadata: IPagination | null;
  users: IUser[];
  allRoles: IRole[];
  roles: IRole[];
  allTeams: ITeam[];
  allLabels: ILabel[];
  /**
   * Deliberately not the shared `loading` boolean above. That one is written by
   * six other settings screens, so a label read finishing would clear a spinner
   * the users or roles page is still waiting on.
   */
  labelsLoading: boolean;
  /** For the inbox filter; see FetchLabelOptions. */
  labelOptions: ILabelOption[];
  labelOptionsLoading: boolean;
  /**
   * The `{ id, name }` list from `GET /v1/teams/options`, for filter pickers.
   * Kept apart from `allTeams`: that one is the permission-gated settings payload with
   * members, and a picker reading it would be empty for anyone without
   * `access:settings_team`.
   */
  teamOptions: ITeamOption[];
  allModules: IModule[];
  notifications: INotification[];
  loading: boolean;
  customFields: ICustomField[];
  customFieldsMetadata: IPagination | null;
  objectCustomFields: IObjectCustomFields;
  ordersSettings: IOrdersSettings | null;
  splittingRules: ISplittingRule[];
  splittingRulesLoading: boolean;
}

export const initialSettingsState: SettingsState = {
  me: null,
  allUsers: [],
  metadata: null,
  users: [],
  allRoles: [],
  roles: [],
  allTeams: [],
  allLabels: [],
  labelsLoading: false,
  labelOptions: [],
  labelOptionsLoading: false,
  teamOptions: [],
  allModules: [],
  notifications: [],
  loading: false,
  customFields: [],
  customFieldsMetadata: null,
  objectCustomFields: {
    values: {},
    definitions: [],
  },
  ordersSettings: null,
  splittingRules: [],
  splittingRulesLoading: false,
};

const featureReducer = createReducer(
  initialSettingsState,
  on(settingsActions.GetMeSuccess, (state, action) => ({
    ...state,
    me: action.user,
  })),
  on(settingsActions.UpdateMeSuccess, (state, action) => ({
    ...state,
    me: action.user,
  })),
  on(settingsActions.FetchAllUsersSuccess, (state, action) => ({
    ...state,
    allUsers: action.users,
  })),
  on(settingsActions.FetchUsersSuccess, (state, action) => ({
    ...state,
    users: action.users.data,
    metadata: action.users.metadata,
  })),

  on(settingsActions.AddUserSuccess, (state, action) => ({
    ...state,
    allUsers: [...state.allUsers, action.user as IUser],
  })),
  on(settingsActions.UpdateUserSuccess, (state, action) => ({
    ...state,
    users: state.users.map((user) =>
      user.id === action.user.id ? { ...user, ...action.user } : user
    )
  })),
  on(settingsActions.SetUsersLoader, (state, action) => ({
    ...state,
    loading: action.isLoading,
  })),

  on(settingsActions.FetchAllRolesSuccess, (state, action) => ({
    ...state,
    allRoles: action.roles,
  })),
  // on(settingsActions.AddRoleSuccess, (state, action) => ({
  //   ...state,
  //   allRoles: [...state.allRoles, action.role as IRole]
  // })),
  // on(
  //   settingsActions.UpdateRoleSuccess,
  //   (state, action) => ({
  //     ...state,
  //     allRoles: state.allRoles.map((role: IRole) =>
  //       role.id === action.role.id ? { ...role, ...action.role } : role
  //     )
  //   })
  // ),
  on(settingsActions.SetRolesLoader, (state, action) => ({
    ...state,
    loading: action.isLoading,
  })),
  on(settingsActions.FetchAllTeamsSuccess, (state, action) => ({
    ...state,
    allTeams: action.teams,
  })),
  on(settingsActions.FetchTeamOptionsSuccess, (state, action) => ({
    ...state,
    teamOptions: action.teams,
  })),
  // `loading` is one shared boolean across the whole settings feature — pre-existing
  // behaviour, mirrored here rather than forked.
  on(settingsActions.SetTeamsLoader, (state, action) => ({
    ...state,
    loading: action.isLoading,
  })),
  on(settingsActions.FetchLabelOptions, (state) => ({
    ...state,
    labelOptionsLoading: true,
  })),
  on(settingsActions.FetchLabelOptionsSuccess, (state, action) => ({
    ...state,
    labelOptions: action.labels,
    labelOptionsLoading: false,
  })),
  on(settingsActions.FetchLabelOptionsError, (state) => ({
    ...state,
    labelOptionsLoading: false,
  })),
  on(settingsActions.FetchAllLabelsSuccess, (state, action) => ({
    ...state,
    allLabels: action.labels,
  })),
  // Its own flag, not the shared `loading` the rest of this reducer writes — see
  // `SetLabelsLoader` in settings.actions.ts for why.
  on(settingsActions.SetLabelsLoader, (state, action) => ({
    ...state,
    labelsLoading: action.isLoading,
  })),
  // The row is dropped here as well as by the refetch the effect triggers, so the
  // table does not keep rendering a label the user has already confirmed deleting.
  on(settingsActions.DeleteLabelSuccess, (state, action) => ({
    ...state,
    allLabels: state.allLabels.filter((label) => label.id !== action.id),
  })),
  on(settingsActions.UpdateLabelSuccess, (state, action) => ({
    ...state,
    allLabels: state.allLabels.map((label) =>
      label.id === action.label.id ? action.label : label
    ),
  })),
  on(settingsActions.FetchAllModulesSuccess, (state, action) => ({
    ...state,
    allModules: action.modules,
  })),
  on(settingsActions.SetModulesLoader, (state, action) => ({
    ...state,
    loading: action.isLoading,
  })),
  on(settingsActions.getNotificationsSuccess, (state, action) => ({
    ...state,
    notifications: action.notifications,
  })),
  on(settingsActions.SetNotificationsLoader, (state, action) => ({
    ...state,
    loading: action.isLoading,
  })),
  on(settingsActions.SetRolesLoader, (state, action) => ({
    ...state,
    loading: action.isLoading,
  })),
  on(settingsActions.FetchAllCustomFieldsSuccess, (state, action) => ({
    ...state,
    customFields: action.customFields.data,
    customFieldsMetadata: action.customFields.metadata ?? null,
  })),
  on(settingsActions.FetchCustomFieldsPageSuccess, (state, action) => ({
    ...state,
    customFields: action.append ? [...state.customFields, ...action.customFields.data] : action.customFields.data,
    customFieldsMetadata: action.customFields.metadata ?? null,
  })),
  on(settingsActions.AddCustomFieldSuccess, (state, action) => ({
    ...state,
    customFields: [action.customField, ...state.customFields.filter((customField) => customField.id !== action.customField.id)],
    customFieldsMetadata: state.customFieldsMetadata
      ? { ...state.customFieldsMetadata, count: state.customFieldsMetadata.count + 1 }
      : state.customFieldsMetadata,
  })),
  on(settingsActions.UpdateCustomFieldSuccess, (state, action) => ({
    ...state,
    customFields: state.customFields.map((customField) => customField.id === action.customField.id ? action.customField : customField),
  })),
  on(settingsActions.SetCustomFieldsLoader, (state, action) => ({
    ...state,
    loading: action.isLoading,
  })),
  on(settingsActions.GetObjectCustomFieldsSuccess, (state, action) => ({
    ...state,
    objectCustomFields: action.objectCustomFields,
  })),
  on(settingsActions.DeleteCustomFieldSuccess, (state, action) => ({
    ...state,
    customFields: state.customFields.filter((customField) => customField.id !== action.id),
  })),
  on(settingsActions.GetOrdersSettingsSuccess, (state, action) => ({
    ...state,
    ordersSettings: action.settings,
  })),
  on(settingsActions.UpdateOrdersSettingsSuccess, (state, action) => ({
    ...state,
    ordersSettings: action.settings,
  })),
  on(settingsActions.LoadSplittingRules, (state) => ({
    ...state,
    splittingRulesLoading: true,
  })),
  on(settingsActions.LoadSplittingRulesSuccess, (state, action) => ({
    ...state,
    splittingRules: action.rules,
    splittingRulesLoading: false,
  })),
  on(settingsActions.LoadSplittingRulesError, (state) => ({
    ...state,
    splittingRulesLoading: false,
  })),
  on(settingsActions.CreateSplittingRuleSuccess, (state, action) => ({
    ...state,
    splittingRules: [...state.splittingRules, action.rule],
  })),
  on(settingsActions.UpdateSplittingRuleSuccess, (state, action) => ({
    ...state,
    splittingRules: state.splittingRules.map(r => r.id === action.rule.id ? action.rule : r),
  })),
  on(settingsActions.DeleteSplittingRuleSuccess, (state, action) => ({
    ...state,
    splittingRules: state.splittingRules.filter(r => r.id !== action.id),
  }))
);

export function settingsReducer(
  state: SettingsState | undefined,
  action: Action
) {
  return featureReducer(state, action);
}
